import { createRouter, createWebHistory } from 'vue-router'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import ProjectDashboard from '@/views/ProjectDashboard.vue'
import PageEditor from '@/views/PageEditor.vue'
import LoginView from '@/views/LoginView.vue'
import { trackActivity } from '@/utils/activity'

const TOKEN_KEY = 'aannemerplus_token'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // --- Public routes ---
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      meta: { public: true }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { public: true }
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/views/TermsOfServicePage.vue'),
      meta: { public: true }
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('@/views/PrivacyPolicyPage.vue'),
      meta: { public: true }
    },

    // --- Conversational onboarding routes (public) — THE onboarding flow ---
    {
      path: '/onboard',
      name: 'conversational-start',
      component: () => import('@/views/ConversationalOnboarding.vue'),
      meta: { public: true }
    },
    {
      path: '/onboard/chat',
      name: 'conversational-chat',
      component: () => import('@/views/ConversationalOnboarding.vue'),
      meta: { public: true }
    },
    {
      path: '/onboard/preview/:slug',
      name: 'conversational-preview',
      component: () => import('@/views/GeneratorPreviewPage.vue'),
      meta: { public: true }
    },

    // --- Admin routes (authenticated) ---
    {
      path: '/admin',
      component: AdminLayout,
      children: [
        {
          path: '',
          name: 'dashboard',
          component: ProjectDashboard
        },
        {
          path: 'project/:projectId/page/:pageId',
          name: 'page-editor',
          component: PageEditor,
          props: true
        },
        {
          path: 'project/:projectId/menus',
          name: 'menu-editor',
          component: () => import('@/views/MenuEditor.vue'),
          props: true
        },
        {
          path: 'project/:projectId/domains',
          name: 'domain-manager',
          component: () => import('@/views/DomainManager.vue'),
          props: true
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/views/SettingsView.vue')
        }
      ]
    },

    // --- Catch-all: redirect to onboard ---
    {
      path: '/:pathMatch(.*)*',
      redirect: '/onboard'
    }
  ]
})

// --- Navigation guards ---
router.beforeEach((to) => {
  const isPublic = to.meta.public === true
  const hasToken = !!localStorage.getItem(TOKEN_KEY)

  // Unauthenticated users trying to access admin → login
  if (!isPublic && !hasToken) {
    return { name: 'login' }
  }

  // Already logged-in users on login page → admin dashboard
  if (to.name === 'login' && hasToken) {
    return { name: 'dashboard' }
  }

})

// Track navigation for debugging user journeys
router.afterEach((to, from) => {
  trackActivity('navigate', {
    to: to.fullPath,
    from: from.fullPath,
    name: to.name as string,
    params: to.params
  })
})

// Handle dynamic import failures (stale chunk hashes after deploy)
router.onError((error, to) => {
  if (
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Importing a module script failed')
  ) {
    console.warn('[admin] Chunk load error, reloading page for fresh assets...')
    window.location.href = to.fullPath
  }
})

export default router
